import gql from "graphql-tag";
import graphqlClient from "@/http/graphql/card";

export const fragments = {
	Employee: `
		fragment Employee on CminsideEmployee {
			id
      		name
			dob
			code
			email
			phone
			department {
				...Department
			}
			title {
				...Title
			}
			titles {
				id
				name
				department {
					id
					name
				}
			}
			tags {
				id
				name
			}
			user_id
			chat_uid
			is_student
			properties
			employee_points {
				id
				school_year
				semester
				course_point
				school_training_point
				dormitory_training_point
				files
				confirmed
				created_at
				updated_at
				attachs {
					id
					path
					file_name
					extension
				}
			}
		}
	`,
	Department: `
		fragment Department on DepartmentType {
			id
			name
			code
		}
	`,
	Title: `
		fragment Title on TitleType {
			id
			name
		}
	`
};

const ADD_EMPLOYEE = `
	create_employee (
		name: $name,
		code: $code,
		phone: $phone,
		email: $email,
		dob: $dob,
		department_id: $department_id,
		title_id: $title_id,
		user: $user,
		tag_ids: $tag_ids,
		is_student: $is_student,
		title_ids: $title_ids
	) {
		...Employee
	}
`;

const UPDATE_EMPLOYEE = `
	update_employee (
		id: $id,
		name: $name,
		code: $code,
		phone: $phone,
		email: $email,
		dob: $dob,
		department_id: $department_id,
		title_id: $title_id,
		user: $user,
		tag_ids: $tag_ids,
		is_student: $is_student,
		title_ids: $title_ids
	) {
		...Employee
	}
`;

const DELETE_EMPLOYEE = `
	delete_employee (id: $id) {
		...Employee
	}
`;

const BLOCK_ACCOUNT = `
	lock_unlock (id: $id, lock: $lock) {
		id
		user {
			can_delete
			can_update
			id
			is_activated
		}
	}
`;

const CREATE_ACCOUNT = `
	create_user_from_employee (id: $id) {
		id
		user {
			id
			is_activated
			can_delete
			can_update
		}
	}
`;

const RESET_PASSWORD = `
	update_user_password(id: $id,password: $password) {
    	id
  	}`;

const CONFIRM_EMPLOYEE_POINTS = `
	confirm_employee_point(id: $id) {
        id
        school_year
        semester
        course_point
        school_training_point
        dormitory_training_point
        files
        confirmed
        created_at
        updated_at
		attachs {
			id
			path
			file_name
			extension
		}
    }`;

const CREATE_EXTRA_POINTS = `
	create_employee_extra_point(employee_id: $employeeId, school_year: $schoolYear, semester: $semester, extra_point: $extraPoint, notes: $notes, event_id: $eventId) {
		id
		school_year
		semester
		extra_point
		notes
		event_id
		created_at
		updated_at
	}`;

const UPDATE_EXTRA_POINTS = `
	update_employee_extra_point(id: $id, employee_id: $employeeId, school_year: $schoolYear, semester: $semester, extra_point: $extraPoint, notes: $notes, event_id: $eventId) {
		id
		school_year
		semester
		extra_point
		notes
		event_id
		created_at
		updated_at
	}`;

export default {
	init() {},

	addEmployee(payload) {
		return graphqlClient
			.mutate({
				mutation: gql`mutation EmployeesManagementCreate (
					$name: String!,
					$code: String,
					$dob: DateTime,
					$email: String!,
					$phone: String!,
					$title_id: Int,
					$department_id: Int,
					$user: UserInput,
					$tag_ids: [Int],
					$is_student: Boolean,
					$title_ids: [Int]
					) {
						${ADD_EMPLOYEE}
					}
					${fragments.Employee}
					${fragments.Department}
					${fragments.Title}
				`,
				variables: {
					name: payload.name,
					code: payload.code,
					department_id: payload.department_id,
					title_id: payload.title_id,
					dob: payload.dob,
					email: payload.email,
					phone: payload.phone,
					user: payload.user,
					tag_ids: payload.tag_ids || [],
					is_student: payload.is_student,
					title_ids: payload.title_ids || []
				}
			})
			.then(response => response.create_employee);
	},

	updateEmployee(payload) {
		return graphqlClient
			.mutate({
				mutation: gql`mutation EmployeesManagementUpdate (
					$id: Int!,
					$name: String!,
					$code: String,
					$dob: DateTime,
					$email: String!,
					$phone: String!,
					$title_id: Int,
					$department_id: Int,
					$user: UserInput,
					$tag_ids: [Int],
					$is_student: Boolean,
					$title_ids: [Int]
					) {
						${UPDATE_EMPLOYEE}
					}
					${fragments.Employee}
					${fragments.Department}
					${fragments.Title}
				`,
				variables: {
					id: payload.id,
					name: payload.name,
					code: payload.code,
					department_id: payload.department_id,
					title_id: payload.title_id,
					dob: payload.dob,
					email: payload.email,
					phone: payload.phone,
					user: payload.user,
					tag_ids: payload.tag_ids || [],
					is_student: payload.is_student,
					title_ids: payload.title_ids || []
				}
			})
			.then(response => response.data.update_employee);
	},

	deleteEmployee(employeeId) {
		return graphqlClient
			.mutate({
				mutation: gql`mutation EmployeesManagementDelete ($id: Int!) {
					${DELETE_EMPLOYEE}
				}
				${fragments.Employee}
				${fragments.Department}
				${fragments.Title}
			`,
				variables: {
					id: employeeId
				}
			})
			.then(response => response.delete_employee);
	},
	blockAccount(payload) {
		return graphqlClient
			.mutate({
				mutation: gql`mutation EmployeeBlockAccount ($id: Int!, $lock: Boolean!) {
					${BLOCK_ACCOUNT}
				}
			`,
				variables: {
					id: payload.id,
					lock: payload.block
				}
			})
			.then(response => response.data.lock_unlock);
	},
	createAccount(payload) {
		return graphqlClient
			.mutate({
				mutation: gql`mutation EmployeeCreateAccount ($id: Int!) {
					${CREATE_ACCOUNT}
				}
			`,
				variables: {
					id: payload.id
				}
			})
			.then(response => response.data.create_user_from_employee);
	},
	resetPassword(payload) {
		return graphqlClient
			.mutate({
				mutation: gql`mutation EmployeeResetPassword ($id: Int!, $password: String!) {
					${RESET_PASSWORD}
				}
			`,
				variables: {
					id: payload.id,
					password: payload.password
				}
			})
			.then(response => response.data.update_user_password);
	},
	confirmEmployeePoint(payload) {
		const queryOptions = {
			mutation: gql`mutation ConfirmPoint ($id: Int!) {
					${CONFIRM_EMPLOYEE_POINTS}
				}
			`,
			variables: {
				id: payload
			}
		};
		return graphqlClient.mutate(queryOptions).then(response => response.data.confirm_employee_point);
	},
	createExtraPoint(payload) {
		const queryOptions = {
			mutation: gql`mutation CreateExtraPoint ($employeeId: Int!, $schoolYear: Int!, $semester: Int!, $extraPoint: Float, $notes: String, $eventId: Int) {
					${CREATE_EXTRA_POINTS}
				}
			`,
			variables: {
				employeeId: payload.employee_id,
				schoolYear: payload.school_year,
				semester: payload.semester,
				extraPoint: payload.extra_point,
				notes: payload.notes,
				eventId: payload.event_id
			}
		}
		return graphqlClient.mutate(queryOptions).then(response => response.data.create_employee_extra_point);
	},
	updateExtraPoint(payload) {
		const queryOptions = {
			mutation: gql`mutation UpdateExtraPoint ($id: Int!, $employeeId: Int!, $schoolYear: Int!, $semester: Int!, $extraPoint: Float, $notes: String, $eventId: Int) {
					${UPDATE_EXTRA_POINTS}
				}
			`,
			variables: {
				id: payload.id,
				employeeId: payload.employee_id,
				schoolYear: payload.school_year,
				semester: payload.semester,
				extraPoint: payload.extra_point,
				notes: payload.notes,
				eventId: payload.event_id
			}
		}
		return graphqlClient.mutate(queryOptions).then(response => response.data.update_employee_extra_point);
	},
	deleteExtraPoint(payload) {
		const queryOptions = {
			mutation: gql`mutation DeleteExtraPoint ($id: Int!) {
					delete_employee_extra_point(id: $id)
				}
			`,
			variables: {
				id: payload
			}
		}
		return graphqlClient.mutate(queryOptions).then(response => response.data.delete_employee_extra_point);
	}
};
